/**
 * ChronicleDetailSectionsEditor — DEV-1336 (#3a: rehome editing).
 *
 * Inline editor for the four legacy content areas now living on the detail page:
 * Key Players (Team), Timeline, Links, Outcomes. Mirrors the legacy
 * ChronicleEditorPage field patterns and reuses the shared TeamMemberEditor, so
 * the legacy full-page editor can retire (DEV-1343).
 *
 * Persists via updateChronicle with replace-all collections — the same pattern
 * the ChronicleSettingsModal uses for sources/classifications/contexts. Reuses
 * the existing chronicleEditor.* labels (no new i18n).
 */

import { useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import TeamMemberEditor, { type TeamMemberData, createBlankTeamMember } from '../TeamMemberEditor/TeamMemberEditor';
import { updateChronicle, type ChronicleResponse } from '../../api/chronicles';

const LINK_TYPES = ['website', 'instagram', 'x', 'facebook', 'youtube', 'news', 'reference', 'l4e_project', 'other'] as const;

interface TimelineRow {
  id: string;
  eventDate: string;
  eventTime: string;
  eventStatus: string;
  eventTitle: string;
  description: string;
  address: string;
}
interface LinkRow { id: string; url: string; label: string; linkType: string; }
interface MetricRow { id: string; label: string; targetValue: string; actualValue: string; unit: string; }

type EditorSectionKey = 'team' | 'timeline' | 'links' | 'outcomes' | 'news';
const ALL_EDITOR_SECTIONS: EditorSectionKey[] = ['team', 'timeline', 'links', 'outcomes', 'news'];

interface Props {
  chronicle: ChronicleResponse;
  /** Which sections this editor manages (default: all). Each tab edits its own. */
  include?: EditorSectionKey[];
  onSaved: () => void;
  onCancel: () => void;
}

export default function ChronicleDetailSectionsEditor({ chronicle, include = ALL_EDITOR_SECTIONS, onSaved, onCancel }: Props) {
  const { t } = useAppTranslation('pages');
  const has = (k: EditorSectionKey) => include.includes(k);

  const [team, setTeam] = useState<TeamMemberData[]>(
    [...(chronicle.teamCredits ?? [])]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((tc, i) => ({
        id: `tm-${tc.creditGuid || i}`,
        personName: tc.personName ?? '',
        roleTitle: tc.roleTitle ?? '',
        photoUrl: tc.manualPhotoUrl ?? '',
        externalProfileUrl: tc.externalProfileUrl ?? '',
        linkedUserGuid: tc.userGuid ?? null,
        linkedUserDisplayName: tc.userGuid ? (tc.personName ?? null) : null,
        sortOrder: i,
      })),
  );
  const [timeline, setTimeline] = useState<TimelineRow[]>(
    [...(chronicle.timelineEvents ?? [])]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((e, i) => ({
        id: `te-${e.eventGuid || i}`,
        eventDate: e.eventDate ?? '',
        eventTime: e.eventTime ?? '',
        eventStatus: e.eventStatus || 'upcoming',
        eventTitle: e.eventTitle ?? '',
        description: e.description ?? '',
        address: e.address ?? '',
      })),
  );
  const [links, setLinks] = useState<LinkRow[]>(
    [...(chronicle.externalLinks ?? [])]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((l, i) => ({ id: `el-${l.linkGuid || i}`, url: l.url ?? '', label: l.label ?? '', linkType: l.linkType || 'website' })),
  );
  const [metrics, setMetrics] = useState<MetricRow[]>(
    [...(chronicle.outcomeMetrics ?? [])]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((m, i) => ({
        id: `om-${m.metricGuid || i}`,
        label: m.label ?? '',
        targetValue: m.targetValue != null ? String(m.targetValue) : '',
        actualValue: m.actualValue != null ? String(m.actualValue) : '',
        unit: m.unit ?? '',
      })),
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Team handlers ──
  const addTeam = () => setTeam((p) => [...p, createBlankTeamMember(p.length)]);
  const changeTeam = (id: string, field: keyof TeamMemberData, value: string | number | null) =>
    setTeam((p) => p.map((m) => (m.id === id ? { ...m, [field]: value } : m)));
  const removeTeam = (id: string) => setTeam((p) => p.filter((m) => m.id !== id));

  // ── Timeline handlers ──
  const addTimeline = () => setTimeline((p) => [...p, { id: `te-${Date.now()}-${p.length}`, eventDate: '', eventTime: '', eventStatus: 'upcoming', eventTitle: '', description: '', address: '' }]);
  const changeTimeline = (id: string, patch: Partial<TimelineRow>) => setTimeline((p) => p.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  const removeTimeline = (id: string) => setTimeline((p) => p.filter((e) => e.id !== id));

  // ── Link handlers ──
  const addLink = () => setLinks((p) => [...p, { id: `el-${Date.now()}-${p.length}`, url: '', label: '', linkType: 'website' }]);
  const changeLink = (id: string, patch: Partial<LinkRow>) => setLinks((p) => p.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const removeLink = (id: string) => setLinks((p) => p.filter((l) => l.id !== id));

  // ── Metric handlers ──
  const addMetric = () => setMetrics((p) => [...p, { id: `om-${Date.now()}-${p.length}`, label: '', targetValue: '', actualValue: '', unit: '' }]);
  const changeMetric = (id: string, patch: Partial<MetricRow>) => setMetrics((p) => p.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  const removeMetric = (id: string) => setMetrics((p) => p.filter((m) => m.id !== id));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const num = (s: string) => (s.trim() === '' ? undefined : Number(s));
    try {
      // Only send the collections this editor manages — updateChronicle replaces
      // only provided collections, so the other tabs' data is left untouched.
      await updateChronicle(chronicle.chronicleGuid, {
        ...(has('team') ? {
          teamCredits: team
            .filter((m) => (m.linkedUserDisplayName || m.personName).trim())
            .map((m, i) => ({
              personName: (m.linkedUserDisplayName || m.personName).trim(),
              roleTitle: m.roleTitle.trim() || undefined,
              userGuid: m.linkedUserGuid || undefined,
              externalProfileUrl: m.externalProfileUrl.trim() || undefined,
              manualPhotoUrl: m.photoUrl.trim() || undefined,
              sortOrder: i,
            })),
        } : {}),
        ...(has('timeline') ? {
          timelineEvents: timeline
            .filter((e) => e.eventTitle.trim())
            .map((e, i) => ({
              dateLabel: e.eventDate || '',
              eventDate: e.eventDate || undefined,
              eventTitle: e.eventTitle.trim(),
              description: e.description.trim() || undefined,
              eventTime: e.eventTime || undefined,
              eventStatus: e.eventStatus || undefined,
              address: e.address.trim() || undefined,
              sortOrder: i,
            })),
        } : {}),
        ...((has('links') || has('news')) ? {
          externalLinks: links
            .filter((l) => l.url.trim())
            .map((l, i) => ({ url: l.url.trim(), label: l.label.trim() || l.url.trim(), linkType: l.linkType || 'website', sortOrder: i })),
        } : {}),
        ...(has('outcomes') ? {
          outcomeMetrics: metrics
            .filter((m) => m.label.trim())
            .map((m, i) => ({ label: m.label.trim(), targetValue: num(m.targetValue), actualValue: num(m.actualValue), unit: m.unit.trim() || undefined, sortOrder: i })),
        } : {}),
      });
      onSaved();
    } catch (e: any) {
      setError(e?.message || 'Save failed');
      setSaving(false);
    }
  };

  // ── Styles ──
  const sectionTitle: React.CSSProperties = { fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBlockEnd: 8 };
  const field: React.CSSProperties = { padding: '8px 11px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: 14 };
  const cell: React.CSSProperties = { ...field, flex: '1 1 140px', minWidth: 0 };
  const row: React.CSSProperties = { display: 'flex', gap: 8, marginBlockEnd: 8, flexWrap: 'wrap', alignItems: 'center' };
  const addBtn: React.CSSProperties = { padding: '6px 12px', borderRadius: 8, border: '1px solid var(--accent-border)', background: 'var(--accent-bg)', color: 'var(--accent-text)', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
  const removeBtn: React.CSSProperties = { padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-tertiary)', fontSize: 12, cursor: 'pointer' };
  const block: React.CSSProperties = { borderBlockStart: '1px solid var(--border-subtle)', paddingBlockStart: 16, marginBlockStart: 16 };

  return (
    <div style={{ border: '1px solid var(--border-default)', borderRadius: 10, padding: 16, background: 'var(--bg-surface)' }}>
      {/* Key Players */}
      {has('team') && (
      <div>
        <div style={sectionTitle}>{t('chronicleEditor.sectionTeam')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {team.map((m, idx) => (
            <TeamMemberEditor
              key={m.id}
              member={m}
              onChange={changeTeam}
              onRemove={removeTeam}
              uploadCategory="chronicle-team-photos"
              uploadEntityId={chronicle.chronicleGuid}
              indexLabel={`${t('chronicleEditor.teamMemberLabel')} ${idx + 1}`}
            />
          ))}
        </div>
        <button type="button" style={{ ...addBtn, marginBlockStart: 8 }} onClick={addTeam}>{t('chronicleEditor.addTeamMember')}</button>
      </div>
      )}

      {/* Timeline */}
      {has('timeline') && (
      <div style={has('team') ? block : undefined}>
        <div style={sectionTitle}>{t('chronicleEditor.sectionTimeline')}</div>
        {timeline.map((e) => (
          <div key={e.id} style={{ ...row, flexDirection: 'column', alignItems: 'stretch', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: 10 }}>
            <div style={row}>
              <input type="date" style={{ ...cell, flex: '0 0 150px' }} value={e.eventDate} onChange={(ev) => changeTimeline(e.id, { eventDate: ev.target.value })} />
              <input type="time" style={{ ...cell, flex: '0 0 120px' }} value={e.eventTime} onChange={(ev) => changeTimeline(e.id, { eventTime: ev.target.value })} />
              <select style={{ ...cell, flex: '0 0 140px' }} value={e.eventStatus} onChange={(ev) => changeTimeline(e.id, { eventStatus: ev.target.value })}>
                <option value="completed">{t('chronicleEditor.timelineStatusCompleted')}</option>
                <option value="current">{t('chronicleEditor.timelineStatusCurrent')}</option>
                <option value="upcoming">{t('chronicleEditor.timelineStatusUpcoming')}</option>
              </select>
            </div>
            <input style={field} value={e.eventTitle} onChange={(ev) => changeTimeline(e.id, { eventTitle: ev.target.value })} placeholder={t('chronicleEditor.timelineEventTitle')} />
            <textarea style={{ ...field, resize: 'vertical' }} rows={2} value={e.description} onChange={(ev) => changeTimeline(e.id, { description: ev.target.value })} placeholder={t('chronicleEditor.timelineDescription')} />
            <input style={field} value={e.address} onChange={(ev) => changeTimeline(e.id, { address: ev.target.value })} placeholder={t('chronicleEditor.timelineAddress')} />
            <button type="button" style={{ ...removeBtn, alignSelf: 'flex-start' }} onClick={() => removeTimeline(e.id)}>{t('chronicleEditor.remove')}</button>
          </div>
        ))}
        <button type="button" style={{ ...addBtn, marginBlockStart: 4 }} onClick={addTimeline}>{t('chronicleEditor.addTimelineEvent')}</button>
      </div>
      )}

      {/* Links (incl. news — both live on the externalLinks array, split by linkType) */}
      {(has('links') || has('news')) && (
      <div style={has('team') || has('timeline') ? block : undefined}>
        <div style={sectionTitle}>{t('chronicleEditor.sectionLinks')}</div>
        {links.map((l) => (
          <div key={l.id} style={row}>
            <input style={cell} value={l.url} onChange={(ev) => changeLink(l.id, { url: ev.target.value })} placeholder={t('chronicleEditor.linkUrl')} />
            <input style={cell} value={l.label} onChange={(ev) => changeLink(l.id, { label: ev.target.value })} placeholder={t('chronicleEditor.linkLabel')} />
            <select style={{ ...cell, flex: '0 0 140px' }} value={l.linkType} onChange={(ev) => changeLink(l.id, { linkType: ev.target.value })}>
              {LINK_TYPES.map((lt) => (
                <option key={lt} value={lt}>{t(`chronicleEditor.linkType.${lt}`)}</option>
              ))}
            </select>
            <button type="button" style={removeBtn} onClick={() => removeLink(l.id)}>{t('chronicleEditor.remove')}</button>
          </div>
        ))}
        <button type="button" style={{ ...addBtn, marginBlockStart: 4 }} onClick={addLink}>{t('chronicleEditor.addLink')}</button>
      </div>
      )}

      {/* Outcomes */}
      {has('outcomes') && (
      <div style={has('team') || has('timeline') || has('links') ? block : undefined}>
        <div style={sectionTitle}>{t('chronicleEditor.sectionMetrics')}</div>
        {metrics.map((m) => (
          <div key={m.id} style={row}>
            <input style={cell} value={m.label} onChange={(ev) => changeMetric(m.id, { label: ev.target.value })} placeholder={t('chronicleEditor.metricLabel')} />
            <input type="number" style={{ ...cell, flex: '0 0 110px' }} value={m.targetValue} onChange={(ev) => changeMetric(m.id, { targetValue: ev.target.value })} placeholder={t('chronicleEditor.metricTarget')} />
            <input type="number" style={{ ...cell, flex: '0 0 110px' }} value={m.actualValue} onChange={(ev) => changeMetric(m.id, { actualValue: ev.target.value })} placeholder={t('chronicleEditor.metricActual')} />
            <input style={{ ...cell, flex: '0 0 100px' }} value={m.unit} onChange={(ev) => changeMetric(m.id, { unit: ev.target.value })} placeholder={t('chronicleEditor.metricUnit')} />
            <button type="button" style={removeBtn} onClick={() => removeMetric(m.id)}>{t('chronicleEditor.remove')}</button>
          </div>
        ))}
        <button type="button" style={{ ...addBtn, marginBlockStart: 4 }} onClick={addMetric}>{t('chronicleEditor.addMetric')}</button>
      </div>
      )}

      {error && <div style={{ color: 'var(--color-error)', fontSize: 13, marginBlockStart: 12 }}>{error}</div>}

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBlockStart: 16 }}>
        <button type="button" onClick={onCancel} disabled={saving} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: 600 }}>{t('chronicleSettings.cancel')}</button>
        <button type="button" onClick={handleSave} disabled={saving} style={{ padding: '8px 16px', borderRadius: 8, border: 0, background: 'var(--accent-primary)', color: '#fff', cursor: saving ? 'wait' : 'pointer', fontWeight: 600 }}>{t('chronicleSettings.save')}</button>
      </div>
    </div>
  );
}
